import * as React from "react";
import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch
} from "@mui/material";
import {
    PlayArrow,
    Close,
    Language
} from "@mui/icons-material";

function WdHelp({ open, onClose }) {
    const [videoLanguage, setVideoLanguage] = useState(false); // false = English, true = Hindi

    const toggleVideoLanguage = () => {
        setVideoLanguage(!videoLanguage);
    };

    const handlePlayVideo = (videoUrl) => {
        window.open(videoUrl, '_blank');
    };

    // Video data
    const helpVideos = [
        {
            id: 1,
            title: "Weight Discrepancy Tutorial Hindi Version",
            filename: "https://djslb35m04okm.cloudfront.net/proship_weight_discrepancy_help/WD%20Hindi%20Version.mp4",
            language: "Hindi"
        },
        {
            id: 2,
            title: "Weight Discrepancy Tutorial English Version",
            filename: "https://djslb35m04okm.cloudfront.net/proship_weight_discrepancy_help/WD%20English%20Version.mp4",
            language: "English"
        }
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    backgroundColor: '#ffffff',
                    maxHeight: '85vh',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ 
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #e5e7eb',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" sx={{ 
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: '1.25rem'
                    }}>
                        Weight Discrepancy Guide
                    </Typography>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        backgroundColor: '#f3f4f6',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        border: '1px solid #e5e7eb'
                    }}>
                        <Language sx={{ fontSize: 16, color: '#6b7280' }} />
                        <Typography variant="body2" sx={{ 
                            color: '#374151',
                            fontWeight: 500,
                            fontSize: '0.8rem'
                        }}>
                            {videoLanguage ? 'हिंदी' : 'English'}
                        </Typography>
                        <Switch
                            checked={videoLanguage}
                            onChange={toggleVideoLanguage}
                            size="small"
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#3b82f6',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#3b82f6',
                                },
                            }}
                        />
                    </Box>
                </Box>
                <IconButton 
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: '#6b7280',
                        '&:hover': {
                            backgroundColor: '#f3f4f6',
                            color: '#374151'
                        }
                    }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2, overflow: 'hidden' }}>
                {helpVideos
                    .filter(video => videoLanguage ? video.language === 'Hindi' : video.language === 'English')
                    .map((video) => (
                    <Box key={video.id}>
                        {/* Video Player */}
                        <Box sx={{ 
                            position: 'relative',
                            mb: 2,
                            borderRadius: 1,
                            overflow: 'hidden',
                            backgroundColor: '#000000',
                            border: '1px solid #e5e7eb'
                        }}>
                            <video
                                controls
                                style={{ 
                                    width: '100%', 
                                    height: '280px',
                                    objectFit: 'contain'
                                }}
                                preload="metadata"
                            >
                                <source 
                                    src={video.filename} 
                                    type="video/mp4" 
                                />
                                Your browser does not support the video tag.
                            </video>
                        </Box>

                        {/* Video Info */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ 
                                fontWeight: 600,
                                color: '#111827',
                                mb: 0.5,
                                fontSize: '1rem'
                            }}>
                                {video.title}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                                color: '#6b7280',
                                fontSize: '0.875rem',
                                lineHeight: 1.4
                            }}>
                                Complete tutorial guide for weight discrepancy management.
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </DialogContent>

            <DialogActions sx={{ 
                backgroundColor: '#f9fafb',
                borderTop: '1px solid #e5e7eb',
                p: 2,
                justifyContent: 'space-between'
            }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlayArrow />}
                    onClick={() => {
                        const currentVideo = helpVideos.find(video => 
                            videoLanguage ? video.language === 'Hindi' : video.language === 'English'
                        );
                        if (currentVideo) handlePlayVideo(currentVideo.filename);
                    }}
                    sx={{
                        px: 2,
                        py: 1,
                        borderColor: '#d1d5db',
                        color: '#374151',
                        borderRadius: 1,
                        fontWeight: 500,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        '&:hover': {
                            borderColor: '#9ca3af',
                            color: '#111827',
                            backgroundColor: '#f3f4f6',
                        }
                    }}
                >
                    Play in New Tab
                </Button>
                <Button 
                    onClick={onClose}
                    variant="outlined"
                    size="small"
                    sx={{
                        px: 2,
                        py: 1,
                        borderColor: '#d1d5db',
                        color: '#374151',
                        borderRadius: 1,
                        fontWeight: 500,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        '&:hover': {
                            borderColor: '#9ca3af',
                            color: '#111827',
                            backgroundColor: '#f3f4f6',
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default WdHelp;